/**
 * SessionTimeoutModal — 55-minute idle warning dialog.
 *
 * Rendered by PortalShell when `useAuth().showIdleWarning === true`. The modal:
 *   - has `role="alertdialog"` + `aria-modal="true"` + `aria-labelledby`
 *   - traps focus (first button auto-focused on mount)
 *   - Escape closes (treated as "Extend session")
 *   - offers [Extend session] [Log out now]
 *
 * Per rcp-security §B.5 #6 — satisfies WCAG 2.2.1 (Timing Adjustable).
 */

import { useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Clock, LogOut } from 'lucide-react';

export function SessionTimeoutModal(): JSX.Element | null {
  const { showIdleWarning, dismissIdleWarning, logout } = useAuth();
  const extendBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!showIdleWarning) return undefined;
    extendBtnRef.current?.focus();
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        dismissIdleWarning();
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [showIdleWarning, dismissIdleWarning]);

  if (!showIdleWarning) return null;

  return (
    <div className="modal-backdrop" onClick={dismissIdleWarning}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="session-timeout-title"
        aria-describedby="session-timeout-desc"
        className="modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="session-timeout-title">
          <Clock aria-hidden="true" size={20} style={{ marginRight: 8, verticalAlign: '-3px' }} />
          Still there?
        </h2>
        <p id="session-timeout-desc">
          For your security, your session will end in about 5 minutes if you
          don&apos;t interact with the page. Extend your session to keep working.
        </p>
        <div className="modal-actions">
          <button
            ref={extendBtnRef}
            type="button"
            className="btn btn-primary"
            onClick={dismissIdleWarning}
          >
            Extend session
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void logout()}
          >
            <LogOut aria-hidden="true" size={16} />
            Log out now
          </button>
        </div>
      </div>
    </div>
  );
}
